// Equations & Expressions helpers: quadratic and linear equation solving,
// systems of two linear equations, coordinate-geometry basics (slope,
// distance, midpoint, point-slope form), quadratic-specific tools (vertex,
// discriminant, completing the square, factoring), and binomial
// multiplication (FOIL). Distinct from lib/tools/powersRootsCalculators.ts
// (which already has the Logarithm Calculator) - this file is specifically
// equation-solving and expression manipulation.

function formatSignedTerm(value: number, variableLabel: string): string {
    // Formats "x - r" as "(x - 5)" or "(x + 3)" depending on the sign of r,
    // rather than always showing a double-negative like "(x - -3)"
    if (value >= 0) return `(${variableLabel} - ${value})`
    return `(${variableLabel} + ${-value})`
}

// ============================================================
// 1. Quadratic Equation Solver
// ============================================================
export function quadraticEquationSolver(params: { a: unknown; b: unknown; c: unknown }): {
    discriminant: number; root1: number | string; root2: number | string; nature_of_roots: string
} {
    const a = Number(params.a) || 0.0001
    const b = Number(params.b) || 0
    const c = Number(params.c) || 0
    const disc = b * b - 4 * a * c

    if (disc > 0) {
        const sq = Math.sqrt(disc)
        const root1 = (-b + sq) / (2 * a)
        const root2 = (-b - sq) / (2 * a)
        return { discriminant: disc, root1, root2, nature_of_roots: 'two_real' }
    }
    if (disc === 0) {
        const root = -b / (2 * a)
        return { discriminant: disc, root1: root, root2: root, nature_of_roots: 'one_real' }
    }
    const sq = Math.sqrt(-disc)
    const real = -b / (2 * a)
    const imag = sq / (2 * a)
    const root1 = `${real} + ${imag}i`
    const root2 = `${real} - ${imag}i`
    return { discriminant: disc, root1, root2, nature_of_roots: 'complex' }
}

// ============================================================
// 2. Linear Equation Solver (ax + b = c)
// ============================================================
export function linearEquationSolver(params: { a: unknown; b: unknown; c: unknown }): { x: number | string } {
    const a = Number(params.a) || 0
    const b = Number(params.b) || 0
    const c = Number(params.c) || 0
    if (a === 0) {
        return { x: b === c ? 'infinite solutions' : 'no solution' }
    }
    return { x: (c - b) / a }
}

// ============================================================
// 3. System of Two Linear Equations Solver
// ============================================================
export function systemOfLinearEquationsSolver(params: {
    a1: unknown; b1: unknown; c1: unknown; a2: unknown; b2: unknown; c2: unknown
}): { x: number | string; y: number | string } {
    const a1 = Number(params.a1) || 0
    const b1 = Number(params.b1) || 0
    const c1 = Number(params.c1) || 0
    const a2 = Number(params.a2) || 0
    const b2 = Number(params.b2) || 0
    const c2 = Number(params.c2) || 0

    const det = a1 * b2 - a2 * b1
    if (det === 0) {
        return { x: 'no unique solution', y: 'no unique solution' }
    }
    const x = (c1 * b2 - c2 * b1) / det
    const y = (a1 * c2 - a2 * c1) / det
    return { x, y }
}

// ============================================================
// 4. Slope Calculator
// ============================================================
export function slopeCalculator(params: { x1: unknown; y1: unknown; x2: unknown; y2: unknown }): {
    slope: number | string; y_intercept: number | string; equation: string
} {
    const x1 = Number(params.x1) || 0
    const y1 = Number(params.y1) || 0
    const x2 = Number(params.x2) || 0
    const y2 = Number(params.y2) || 0

    if (x1 === x2) {
        return { slope: 'undefined (vertical line)', y_intercept: 'n/a', equation: `x = ${x1}` }
    }
    const slope = (y2 - y1) / (x2 - x1)
    const intercept = y1 - slope * x1
    const equation = `y = ${slope}x ${intercept >= 0 ? '+' : '-'} ${Math.abs(intercept)}`
    return { slope, y_intercept: intercept, equation }
}

// ============================================================
// 5. Distance Between Two Points Calculator
// ============================================================
export function distanceBetweenPointsCalculator(params: { x1: unknown; y1: unknown; x2: unknown; y2: unknown }): { distance: number } {
    const x1 = Number(params.x1) || 0
    const y1 = Number(params.y1) || 0
    const x2 = Number(params.x2) || 0
    const y2 = Number(params.y2) || 0
    return { distance: Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2) }
}

// ============================================================
// 6. Midpoint Calculator
// ============================================================
export function midpointCalculator(params: { x1: unknown; y1: unknown; x2: unknown; y2: unknown }): {
    midpoint_x: number; midpoint_y: number
} {
    const x1 = Number(params.x1) || 0
    const y1 = Number(params.y1) || 0
    const x2 = Number(params.x2) || 0
    const y2 = Number(params.y2) || 0
    return { midpoint_x: (x1 + x2) / 2, midpoint_y: (y1 + y2) / 2 }
}

// ============================================================
// 7. Point-Slope Form Calculator
// ============================================================
export function pointSlopeFormCalculator(params: { x1: unknown; y1: unknown; slope: unknown }): {
    point_slope_form: string; slope_intercept_form: string
} {
    const x1 = Number(params.x1) || 0
    const y1 = Number(params.y1) || 0
    const m = Number(params.slope) || 0
    const intercept = y1 - m * x1
    const pointSlopeForm = `y - ${y1} = ${m}(x - ${x1})`
    const slopeInterceptForm = `y = ${m}x ${intercept >= 0 ? '+' : '-'} ${Math.abs(intercept)}`
    return { point_slope_form: pointSlopeForm, slope_intercept_form: slopeInterceptForm }
}

// ============================================================
// 8. Vertex of a Parabola Calculator (standard form ax² + bx + c)
// ============================================================
export function parabolaVertexCalculator(params: { a: unknown; b: unknown; c: unknown }): {
    vertex_x: number; vertex_y: number; vertex_form: string
} {
    const a = Number(params.a) || 0.0001
    const b = Number(params.b) || 0
    const c = Number(params.c) || 0
    const h = -b / (2 * a)
    const k = c - (b * b) / (4 * a)
    const vertexForm = `y = ${a}${formatSignedTerm(h, 'x')}² ${k >= 0 ? '+' : '-'} ${Math.abs(k)}`
    return { vertex_x: h, vertex_y: k, vertex_form: vertexForm }
}

// ============================================================
// 9. Discriminant Calculator
// ============================================================
const DISCRIMINANT_TWO_REAL: Record<string, string> = {
    en: 'Two distinct real roots', ru: 'Два различных действительных корня', de: 'Zwei verschiedene reelle Nullstellen', es: 'Dos raíces reales distintas', fr: 'Deux racines réelles distinctes', it: 'Due radici reali distinte', pl: 'Dwa różne pierwiastki rzeczywiste', lv: 'Divas atšķirīgas reālas saknes',
}
const DISCRIMINANT_ONE_REAL: Record<string, string> = {
    en: 'One repeated real root', ru: 'Один повторяющийся действительный корень', de: 'Eine doppelte reelle Nullstelle', es: 'Una raíz real repetida', fr: 'Une racine réelle double', it: 'Una radice reale ripetuta', pl: 'Jeden podwójny pierwiastek rzeczywisty', lv: 'Viena atkārtota reāla sakne',
}
const DISCRIMINANT_COMPLEX: Record<string, string> = {
    en: 'Two complex conjugate roots', ru: 'Два комплексно-сопряжённых корня', de: 'Zwei komplex konjugierte Nullstellen', es: 'Dos raíces complejas conjugadas', fr: 'Deux racines complexes conjuguées', it: 'Due radici complesse coniugate', pl: 'Dwa pierwiastki zespolone sprzężone', lv: 'Divas kompleksi saistītas saknes',
}
export function discriminantCalculator(params: { a: unknown; b: unknown; c: unknown; language: unknown }): {
    discriminant: number; nature_of_roots: string
} {
    const a = Number(params.a) || 0
    const b = Number(params.b) || 0
    const c = Number(params.c) || 0
    const disc = b * b - 4 * a * c
    const lang = String(params.language || 'en') as keyof typeof DISCRIMINANT_TWO_REAL
    let nature: string
    if (disc > 0) nature = DISCRIMINANT_TWO_REAL[lang] ?? DISCRIMINANT_TWO_REAL.en
    else if (disc === 0) nature = DISCRIMINANT_ONE_REAL[lang] ?? DISCRIMINANT_ONE_REAL.en
    else nature = DISCRIMINANT_COMPLEX[lang] ?? DISCRIMINANT_COMPLEX.en
    return { discriminant: disc, nature_of_roots: nature }
}

// ============================================================
// 10. Completing the Square Calculator
// ============================================================
export function completingTheSquareCalculator(params: { a: unknown; b: unknown; c: unknown }): {
    h: number; k: number; completed_square_form: string
} {
    const a = Number(params.a) || 0.0001
    const b = Number(params.b) || 0
    const c = Number(params.c) || 0
    const h = -b / (2 * a)
    const k = c - (b * b) / (4 * a)
    const completedSquareForm = `${a}${formatSignedTerm(h, 'x')}² ${k >= 0 ? '+' : '-'} ${Math.abs(k)}`
    return { h, k, completed_square_form: completedSquareForm }
}

// ============================================================
// 11. Quadratic Factoring Calculator
// ============================================================
export function quadraticFactoringCalculator(params: { a: unknown; b: unknown; c: unknown }): {
    factored_form: string; is_factorable_over_reals: string
} {
    const a = Number(params.a) || 0.0001
    const b = Number(params.b) || 0
    const c = Number(params.c) || 0
    const disc = b * b - 4 * a * c

    if (disc < 0) {
        return { factored_form: 'Not factorable over the real numbers', is_factorable_over_reals: 'No' }
    }
    const sq = Math.sqrt(disc)
    const r1 = (-b + sq) / (2 * a)
    const r2 = (-b - sq) / (2 * a)
    const factoredForm = `${a}${formatSignedTerm(r1, 'x')}${formatSignedTerm(r2, 'x')}`
    return { factored_form: factoredForm, is_factorable_over_reals: 'Yes' }
}

// ============================================================
// 12. FOIL / Binomial Multiplication Calculator
// (a1*x + b1)(a2*x + b2)
// ============================================================
export function binomialMultiplicationCalculator(params: {
    a1: unknown; b1: unknown; a2: unknown; b2: unknown
}): { x_squared_coefficient: number; x_coefficient: number; constant_term: number; expanded_form: string } {
    const a1 = Number(params.a1) || 0
    const b1 = Number(params.b1) || 0
    const a2 = Number(params.a2) || 0
    const b2 = Number(params.b2) || 0

    const x2 = a1 * a2
    const x1 = a1 * b2 + a2 * b1
    const x0 = b1 * b2

    const expandedForm = `${x2}x² ${x1 >= 0 ? '+' : '-'} ${Math.abs(x1)}x ${x0 >= 0 ? '+' : '-'} ${Math.abs(x0)}`
    return { x_squared_coefficient: x2, x_coefficient: x1, constant_term: x0, expanded_form: expandedForm }
}
